import React from "react";

import cx from "classnames";

import { doNothing } from "@kazoohr/helpers";

import styles from "./Tab.scss";

/**
 * A Tab component.
 */
export function Tab({
  active = false,
  children,
  disabled = false,
  href,
  onClick = doNothing,
  value,
  style = "default",
  "data-testid": testId,
}: React.PropsWithChildren<TabProps>) {
  const handleClick = () => {
    onClick(value);
  };

  const TagType = href && !disabled ? "a" : "button";

  const tabButtonClassNames =
    style === "compact" || style === "default"
      ? cx(styles.tab, {
          [styles["tab--active"]]: active,
          [styles["tab--compact"]]: style === "compact",
        })
      : cx(styles["tab-filled"], {
          [styles["tab-filled--active"]]: active,
        });

  return (
    <TagType
      aria-selected={active}
      className={tabButtonClassNames}
      disabled={disabled}
      href={href}
      onClick={handleClick}
      role="tab"
      data-testid={testId}
      type="button"
    >
      {children}
    </TagType>
  );
}

export interface TabProps {
  /**
   * Boolean to set tab to active. Ignores your supplied value. The parent `Tabs` component will overwrite it.
   * @private
   */
  active?: boolean;

  /**
   * Boolean to set tab to disabled.
   */
  disabled?: boolean;

  /**
   * Sets the option to be a link with this `href`.
   */
  href?: string;

  /**
   * Calls a handler within `Tabs` to call its `onChange` prop.
   * @private
   */
  onClick?: (value: string) => void;

  /**
   * Value of the tab to pass to Tabs when clicked.
   */
  value: string;

  /**
   * Style of the tab. Default is "default"
   */
  style?: "default" | "compact" | "filled";

  /**
   * Optional test ID for testing purposes.
   */
  "data-testid"?: string;
}
